import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { withDigitalData } from '../../../utils/DigitalDataContext';
import { withLabels } from '../../../utils/LabelsContext';
import { withProduct } from '../../../utils/ProductContext';
import { ImageGalleryGlobalStyles } from '../ImageGallery.style';
import ImageGalleryCarousel from '../ImageGalleryCarousel/v2';
import ImageGalleryControlButtons from '../ImageGalleryHeadLine/ImageGalleryControlButtons/v2';
import ImageGalleryHeadlineDesktop from '../ImageGalleryHeadLine/ImageGalleryHeadlineDesktop/v2';
import { useMordenImageGallery } from './hooks/useMordenImageGallery';
import { MordenImageGalleryStyles } from './style';

const MordenImageGallery = ({
  product,
  carouselImageHeight,
  carouselImageWidth,
  desktopImagesPerSlide,
  zoomSize,
  imageQuality,
  headlineImageHeight,
  headlineImageWidth,
  imagesPerSlide,
  appCtx,
  labels,
  crop,
  digitalData,
}) => {
  const {
    isOutOfStock,
    brandName,
    name,
    regionCode,
    isDottedImageCarouselEnabled,
    activeImageIndex,
    activeIndicatorIndex,
    carouselImages,
    isVideoPlaying,
    setActiveImageIndex,
    setActiveIndicatorIndex,
    setVideoPlaying,
    onNextHeadline,
    onPreviousHeadLine,
    handleMouseOverButton,
    getImages,
  } = useMordenImageGallery({
    product,
    appCtx,
    labels,
    digitalData,
    desktopImagesPerSlide,
    imagesPerSlide,
  });

  return (
    <div className={cx('modern-image-gallery', { 'modern-image-gallery--out-of-stock': isOutOfStock })}>
      <div className="modern-image-gallery__carousel">
        <ImageGalleryCarousel
          imageQuality={imageQuality}
          carouselImageHeight={carouselImageHeight}
          carouselImageWidth={carouselImageWidth}
          activeImageIndex={activeImageIndex}
          activeIndicatorIndex={activeIndicatorIndex}
          setActiveImageIndex={setActiveImageIndex}
          setActiveIndicatorIndex={setActiveIndicatorIndex}
          images={getImages()}
          imagesPerSlide={imagesPerSlide}
          carouselImages={carouselImages}
          headlineImageHeight={headlineImageHeight}
          headlineImageWidth={headlineImageWidth}
          tenant={regionCode}
          productName={name}
          brandName={brandName}
          isDottedImageCarouselEnabled={isDottedImageCarouselEnabled}
        />
      </div>

      <div className="modern-image-gallery__headline">
        <div className="modern-image-gallery__controls">
          <ImageGalleryControlButtons
            onNextHeadline={onNextHeadline}
            onPreviousHeadLine={onPreviousHeadLine}
            onMoveOver={handleMouseOverButton}
            variant={isVideoPlaying ? 'opaque' : 'transparent'}
          />
        </div>

        <div className="modern-image-gallery__main-image">
          <ImageGalleryHeadlineDesktop
            imageQuality={imageQuality}
            zoomSize={zoomSize}
            activeImageIndex={activeImageIndex}
            images={getImages()}
            imagesPerSlide={desktopImagesPerSlide}
            activeIndicatorIndex={activeIndicatorIndex}
            setActiveIndicatorIndex={setActiveIndicatorIndex}
            setActiveImageIndex={setActiveImageIndex}
            headlineImageHeight={headlineImageHeight}
            headlineImageWidth={headlineImageWidth}
            tenant={regionCode}
            showControlButtons={false}
            crop={crop}
            setVideoPlayingForSoftline={setVideoPlaying}
            productName={name}
            brandName={brandName}
          />
        </div>
      </div>

      <style jsx>{ImageGalleryGlobalStyles}</style>
      <style jsx>{MordenImageGalleryStyles}</style>
    </div>
  );
};

MordenImageGallery.defaultProps = {
  carouselImageHeight: 100,
  carouselImageWidth: 100,
  headlineImageHeight: 650,
  headlineImageWidth: 650,
  desktopImagesPerSlide: 5,
  zoomSize: { x: 1004, y: 1500 },
  imageQuality: 70,
  imagesPerSlide: 5,
  crop: { x: 248, y: 0 },
};

MordenImageGallery.propTypes = {
  product: PropTypes.object.isRequired,
  carouselImageHeight: PropTypes.number,
  carouselImageWidth: PropTypes.number,
  desktopImagesPerSlide: PropTypes.number,
  zoomSize: PropTypes.object,
  imageQuality: PropTypes.number,
  headlineImageWidth: PropTypes.number,
  headlineImageHeight: PropTypes.number,
  imagesPerSlide: PropTypes.number,
  appCtx: PropTypes.object.isRequired,
  crop: PropTypes.object,
  labels: PropTypes.object.isRequired,
  digitalData: PropTypes.object.isRequired,
};

export default withLabels(withProduct(withApplicationContext(withDigitalData(MordenImageGallery))));
export { MordenImageGallery };
